import ImageContextBase from "./imageContextBase";
import store from "../store";
import { SeriesTypes, EyeMarkers, EyeTypes, ImageFormats } from "../config/config";
import { defaultCollectionBounds, getCollectionBounds as calculateCollectionBounds } from "../canvas/geometry";

export default class OCTContextBase extends ImageContextBase {
  constructor(group, exam, componentName, browserName, imgKey) {
    super(exam, componentName, browserName);

    this.group = group;
    this.groupName = group.groupName;
    this.eye = group.eye;
    this.seriesType = group.seriesType;
    this.browserType = group.browserType;
    this.seriesMoment = group.seriesMoment;

    this.seriesFileName = "";
    this.referenceImage = null;

    this.enfaceIsReference = false;
    this.enfaceImage = null;

    this.hasFoveaOffset = false;
    this.foveaOffset = null;

    this.thicknessMap = null;
    this.thicknessAnalysis = null;

    this.sliceInfo = null;
    this.sliceCurrent = null;
    this.sliceBounds = defaultCollectionBounds();

    this.compositeInfo = null;
    this.compositeCurrent = null;
    this.compositeBounds = defaultCollectionBounds();

    this.hdVertInfo = null;
    this.hdVertCurrent = null;
    this.hdVertBounds = defaultCollectionBounds();

    this.hdHorzInfo = null;
    this.hdHorzCurrent = null;
    this.hdHorzBounds = defaultCollectionBounds();

    this.cscanInfo = null;
    this.cscanCurrent = null;
    this.cscanBounds = defaultCollectionBounds();

    this.collectionKeys = {
      data: {
        slice: "sliceCollection",
        composite: "compositeSliceCollection",
        hdVert: "hiDefImageCollectionVertical",
        hdHorz: "hiDefImageCollectionHorizontal",
        cscan: "slabCollection"
      },
      imageType: {
        slice: "bscan",
        composite: "composite",
        hdVert: "hidef",
        hdHorz: "hidef",
        cscan: "cscan"
      },
      segmentationType: {
        slice: "newApiSegmentation",
        composite: "newApiCompositeSegmentation",
        hdVert: "newApiHiDefSegmentation",
        hdHorz: "newApiHiDefSegmentation",
        newApi: "newApi"
      }
    };

    this.octDataLoading = true;

    this.loadComposite = true;
    this.loadHD = true;
    this.loadCScan = true;

    this.dropdownItems = [];
    this.selectedDropdownItem = null;
    this.imgKey = imgKey;
    this.exams = store.getters.octExams.filter(e => e.browserType === this.exam.browserType);
  }

  startupTask() {
    super.startupTask();
    this.generateOCTDropdown();
    return store.dispatch("getOCTInfo", { exSrNo: this.exSrNo, groupName: this.groupName })
      .then(data => this.setOCTInfo(data.data))
      .then(this.refreshImages);
  }

  refreshImages(){
    return store.dispatch("loadOCTImages");
  }

  clearContext() {
    super.clearContext();
    store.commit("clearImageQueue", "octPreviewQueue");
    store.commit("clearImageQueue", "octFullQueue");
    store.commit("clearImageQueue", "octSegmentationQueue");
  }

  setOCTInfo(data) {
    if (!data) return;

    this.seriesFileName = data.seriesFileName;
    this.processImage(data, "referenceImage", "reference");

    this.enfaceisReference = data.enfaceIsReferenceImage;
    this.processImage(data, "enfaceImage", "enface");

    this.processImage(data, "thicknessMap", "newApiColorMap");

    this.processThicknessAnalysis(data);

    this.processFovea(data);

    this.processSlices(data, "slice", this.foveaOffset.y);
    if (this.loadComposite) this.processSlices(data, "composite", this.foveaOffset.x);
    if (this.loadHD) {
      this.processSlices(data, "hdVert", this.foveaOffset.y);
      this.processSlices(data, "hdHorz", this.foveaOffset.x);
    }

    if (this.loadCScan) this.processCScan(data, "cscan", this.foveaOffset.y);
    // bump visual load state completion just a bit, to prevent timing issues when rendering images
    setTimeout(() => this.octDataLoading = false, 20);
  }

  processImage(data, key, imageType) {
    let image = Object.assign({}, data[key]);
    if (!data[key]) return;
    image.fileName = this.seriesFileName;
    image.width = image.pixelWidth || 0;
    image.height = image.pixelHeight || 0;
    image.imageFormat = image.imageFormat || ImageFormats.Color;
    image.keyword = key;
    if (image.dimensions) {
      image.imageFormat = image.dimensions.imageFormat;
      image.width = image.dimensions.pixelWidth;
      image.height = image.dimensions.pixelHeight;
    }
    store.commit("storeOCTImages", { images: [image], context: this, imageType, loadPreviews: false });
    store.commit("selectOCTImage", { imgKey: image.imgKey, currentKey: key });
  }

  processThicknessAnalysis(data) {
    if (data.thicknessAnalysisData) this.thicknessAnalysis = data.thicknessAnalysisData;
  }

  processFovea(data) {
    this.hasFoveaOffset = data.hasFoveaOffset;
    this.foveaOffset = this.hasFoveaOffset ? data.foveaOffset : { x: 0.5, y: 0.5 };
  }

  processSlices(data, key, centerOffset) {
    let dataKey = this.collectionKeys.data[key];
    let collection = data[dataKey];
    if (!collection) return;

    let infoKey = key + "Info";
    this[infoKey] = {
      isZeissCross: collection.isZeissCross,
      segmentation: collection.segmentation,
      sliceHasSegmentation: collection.segmentation && collection.segmentation.layers && collection.segmentation.layers.length,
      dimensions: collection.sliceDimensions
    };

    let composite = dataKey === "compositeSliceCollection";
    let rotate = composite && this.seriesType === SeriesTypes.OpticDisc;
    let od = this.eye === EyeTypes.OD;
    let os = this.eye === EyeTypes.OS;
    let imageType = this.collectionKeys.imageType[key];
    let segmentationImageType = this.collectionKeys.segmentationType[key];
    collection.slices.forEach(image => {
      image.keyword = key;
      image.imageFormat = ImageFormats.Grayscale;
      image.fileName = image.sliceFileName || this.seriesFileName;
      image.width = collection.sliceDimensions.pixelWidth;
      image.height = collection.sliceDimensions.pixelHeight;
      image.imageType = imageType;
      image.segmentationImageType = segmentationImageType;
      image.vertical = image.startPoint && image.endPoint
        && (Math.abs(image.startPoint.x - image.endPoint.x) < 0.0001) || false;
      image.eyeMarker = EyeMarkers.None;

      if ((composite && !rotate) || (!composite && image.vertical && od))
        image.eyeMarker = EyeMarkers.SI;
      else if ((composite && rotate) || (!composite && image.vertical && od))
        image.eyeMarker = EyeMarkers.IS;
      else if (!composite && !image.vertical && od)
        image.eyeMarker = EyeMarkers.TN;
      else if (!composite && !image.vertical && os)
        image.eyeMarker = EyeMarkers.NT;
    });

    let boundsKey = key + "Bounds";
    this[boundsKey] = calculateCollectionBounds(collection.slices);

    store.commit("storeOCTImages", {
      info: this[infoKey],
      images: collection.slices,
      context: this,
      imageType,
      segmentationImageType,
      loadPreviews: true
    });

    let currentKey = key + "Current";
    let keyword = key;
    let initialSlice = Math.floor(collection.slices.length * centerOffset);
    if (collection.slices.length)
      store.commit("selectOCTImage", { imgKey: collection.slices[initialSlice].imgKey, currentKey, keyword });
  }


  processCScan(data, key, centerOffset) {
    let dataKey = this.collectionKeys.data[key];
    let collection = data[dataKey];
    if (!collection) return;

    let infoKey = key + "Info";
    this[infoKey] = {
      dimensions:  collection.slabDimensions
    };

    let imageType = this.collectionKeys.imageType[key];
    let segmentationImageType = this.collectionKeys.segmentationType[key];
    collection.slabs.forEach(image => {
      image.keyword = key;
      image.imageFormat = ImageFormats.Grayscale;
      image.fileName = image.sliceFileName || this.seriesFileName;
      image.width = collection.slabDimensions.pixelWidth;
      image.height = collection.slabDimensions.pixelHeight;
      image.imageType = imageType;
      image.segmentationImageType = segmentationImageType;
    });

    let boundsKey = key + "Bounds";
    this[boundsKey] = calculateCollectionBounds(collection.slices);

    store.commit("storeOCTImages", {
      info: this[infoKey],
      images: collection.slabs,
      context: this,
      imageType,
      loadPreviews: true
    });

    let currentKey = key + "Current";
    let keyword = key;
    let initialSlice = Math.floor(collection.slabs.length * centerOffset);
    if (collection.slabs.length)
      store.commit("selectOCTImage", { imgKey: collection.slabs[initialSlice].imgKey, currentKey, keyword });
  }


  setOCTImage(key, image) {
    this[key] = image;
    if (!image.imgIsLoading && !image.imgHtmlObject && !image.imgNotFound)
      store.dispatch("loadOCTImageImmediately", { image, key, view: this.view });
  }

  generateOCTDropdown() {
    let ex = this.exams;
    let octexamSrNos = ex.map(e => e.exSrNo);
    store.dispatch("loadExamImageMetadata", octexamSrNos)
      .then(() =>{
        let exImages = store.getters.imagesForExams(ex).filter(i => i.imgEye == this.eye && i.imgType != "IMAGE-SLO").sort((a, b) => b.exMoment.diff(a.exMoment));
        exImages.forEach(e =>{
          let item = {};
          item.flSrNo = e.flSrNo;
          item.imgGroup = e.imgGroup;
          item.exSrNo = e.exSrNo;
          if(e.hasOwnProperty('imgData') && e.imgData != "")
            item.octDateButtonText = e.exDateDisplay + " - " + e.imgEye + " " + e.imgData + " (" + e.exSrNo + ") (" + e.flSrNo + ")";
          else
            item.octDateButtonText =  e.exDateDisplay + " - " + e.imgGroup + " (" + e.exSrNo + ") (" + e.flSrNo + ")";
    
            this.dropdownItems.push(item);
        });

        //imgKey is not available when generateContextFromEye. (select OD or OS)
        if(!this.imgKey){
          this.selectedDropdownItem = this.dropdownItems.filter(e => e.exSrNo == this.exSrNo && e.imgGroup == this.group.groupName)[0];
        }
        else{
          let image = store.getters.imageByKey(this.imgKey);
          this.selectedDropdownItem = this.dropdownItems.find(e => e.flSrNo == image.flSrNo);
          if (!this.selectedDropdownItem)
            this.selectedDropdownItem = this.dropdownItems.filter(e => e.exSrNo == this.exSrNo && e.imgGroup == this.group.groupName)[0];
        }
      });
  }
}